/**
 * iter238/239 — Page publique de suivi des véhicules (sans login).
 * Ouverte via un lien secret : /suivi/:token
 * Lecture seule, sauf la saisie du N° d'autorisation (si autorisée sur le lien).
 * Ce que le lien laisse voir (devis, photos, pièces jointes, références,
 * dossiers terminés) est piloté par la visibilité configurée sur le lien.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Car, CheckCircle2, Clock, FileText, ImageIcon, Loader2, Paperclip, RefreshCw, Search, Wrench,
  Lock,
} from "lucide-react";
import PublicMessages from "@/components/public/PublicMessages";
import PublicNotifCard from "@/components/public/PublicNotifCard";
import {
  ETAPES as ETAPES_I18N, LOCALES, langInitiale, memoriseLang, refLabel, statutLabel, traducteur,
} from "@/i18n/suivi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/suivi`;

// iter272 — Lien protégé par mot de passe : le jeton d'accès voyage en en-tête
// pour les appels axios, et en paramètre `acces` pour les images/PDF ouverts
// directement par le navigateur (qui ne peuvent pas porter d'en-tête).
let ACCES = "";
const accesKey = (t) => `suivi-acces-${t}`;
const http = axios.create();
http.interceptors.request.use((cfg) => {
  if (ACCES) cfg.headers["X-Suivi-Acces"] = ACCES;
  return cfg;
});
const media = (url) => (ACCES ? `${url}${url.includes("?") ? "&" : "?"}acces=${encodeURIComponent(ACCES)}` : url);

const fmtDate = (iso, lang = "fr") => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(LOCALES[lang] || "fr-CH", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
};

const fmtDateTime = (iso, lang = "fr") => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(LOCALES[lang] || "fr-CH", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
};

const norm = (s) => String(s || "").toLowerCase().replace(/[\s.-]/g, "");

function Frise({ index, termine, accent, lang }) {
  return (
    <div className="flex items-center gap-1 mt-3" data-testid="suivi-frise">
      {(ETAPES_I18N[lang] || ETAPES_I18N.fr).map((label, i) => {
        const done = termine || i <= index;
        const current = !termine && i === index;
        return (
          <div key={label} className="flex-1 min-w-0">
            <div
              className="h-1.5 rounded-full transition-colors"
              style={{ backgroundColor: done ? accent : "#e5e7eb", opacity: done && !current ? 0.6 : 1 }}
            />
            <div
              className={`mt-1 text-[10px] truncate ${current ? "font-semibold" : "text-gray-400"}`}
              style={current ? { color: accent } : undefined}
            >
              {label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OrCard({ or_, token, accent, vis: visLien, garageNom, onSaved, lang }) {
  const t = traducteur(lang);
  // iter271 — Autorisations propres au client du dossier (un lien peut couvrir
  // plusieurs sociétés avec des droits différents)
  const vis = or_.visibilite || visLien;
  const [aut, setAut] = useState(or_.no_autorisation || "");
  const [saving, setSaving] = useState(false);
  const [enlevant, setEnlevant] = useState(false);
  const [validant, setValidant] = useState(false);
  const dirty = (aut || "").trim() !== (or_.no_autorisation || "");
  const photos = or_.photos || [];

  const saveAut = async () => {
    const value = (aut || "").trim();
    if (!value) return;
    setSaving(true);
    try {
      await http.post(`${API}/${token}/or/${or_.id}/autorisation`, { value });
      toast.success(t("autorisation_ok"));
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("enregistrement_ko"));
    } finally {
      setSaving(false);
    }
  };

  // iter263 — validation du devis d'un clic (clients en mode « bouton »)
  const validerDevis = async () => {
    setValidant(true);
    try {
      await http.post(`${API}/${token}/or/${or_.id}/valider-devis`);
      toast.success(t("devis_valide_ok"));
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("validation_ko"));
    } finally {
      setValidant(false);
    }
  };

  // iter258 — le client confirme lui-même l'enlèvement du véhicule
  const toggleEnleve = async () => {
    setEnlevant(true);
    try {
      await http.post(`${API}/${token}/or/${or_.id}/enleve`, { enleve: !or_.enleve_at });
      toast.success(or_.enleve_at ? t("enlevement_annule") : t("enlevement_ok"));
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("action_ko"));
    } finally {
      setEnlevant(false);
    }
  };

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm"
      data-testid={`suivi-or-${or_.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4 flex-shrink-0" style={{ color: accent }} />
            <span className="font-semibold text-gray-900 truncate" data-testid="suivi-or-plaque">
              {or_.plaque || t("sans_plaque")}
            </span>
          </div>
          <div className="text-sm text-gray-500 truncate">{or_.vehicule || "—"}</div>
          <div className="text-[11px] text-gray-400 mt-0.5">
            {t("dossier")} {or_.numero}
            {or_.client_nom ? ` · ${or_.client_nom}` : ""}
          </div>
        </div>
        <span
          className="px-2 py-1 rounded-full text-[11px] font-medium text-right max-w-[46%] sm:max-w-[60%] flex-shrink-0"
          style={
            or_.termine
              ? { background: "#f3f4f6", color: "#4b5563" }
              : or_.etape_index >= 4
                ? { background: `${accent}26`, color: accent }
                : { background: "#eff6ff", color: "#1d4ed8" }
          }
          data-testid="suivi-or-statut"
        >
          {statutLabel(lang, or_.statut, or_.statut_label)}
        </span>
      </div>

      <Frise index={or_.etape_index} termine={or_.termine} accent={accent} lang={lang} />

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-[11px] text-gray-600">
        <div>{t("depose_le")} <b className="text-gray-900">{fmtDate(or_.date_depose, lang)}</b></div>
        {or_.rdv_prevu && <div>{t("rdv")} <b className="text-gray-900">{fmtDateTime(or_.rdv_prevu, lang)}</b></div>}
        {or_.date_pret && <div>{t("pret_depuis")} <b className="text-gray-900">{fmtDate(or_.date_pret, lang)}</b></div>}
        <div>{t("mise_a_jour")} <b className="text-gray-900">{fmtDate(or_.date_maj, lang)}</b></div>
        {or_.facture_numero && <div>{t("facture")} <b className="text-gray-900">{or_.facture_numero}</b></div>}
      </div>

      {or_.references.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {or_.references.map((r) => (
            <span key={`${r.label}-${r.value}`} className="px-2 py-0.5 bg-gray-50 border border-gray-200 rounded text-[10px] text-gray-600">
              {refLabel(lang, r)} : <b className="text-gray-800">{r.value}</b>
            </span>
          ))}
        </div>
      )}

      {photos.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1.5">
            <ImageIcon className="w-3.5 h-3.5" /> {t("photos_vehicule")}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`suivi-photos-${or_.id}`}>
            {photos.map((p) => {
              const url = media(`${API}/${token}/or/${or_.id}/photo/${p.type}/${p.index}`);
              return (
                <a
                  key={`${p.type}-${p.index}`}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0"
                  data-testid={`suivi-photo-${or_.id}-${p.type}-${p.index}`}
                >
                  <img
                    src={url}
                    alt={`${or_.plaque} ${p.type}`}
                    loading="lazy"
                    className="w-20 h-20 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                  />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {(or_.devis || or_.attachments.length > 0) && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {or_.devis && (
            <a
              href={media(`${API}/${token}/or/${or_.id}/devis.pdf`)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
              data-testid={`suivi-devis-${or_.id}`}
            >
              <FileText className="w-3.5 h-3.5 text-red-600" />
              {t("devis")} — CHF {Number(or_.devis.montant_ttc).toFixed(2)}
              {or_.devis.en_attente && <span className="text-orange-600 font-medium">{t("a_valider")}</span>}
            </a>
          )}
          {or_.attachments.map((a) => (
            <a
              key={a.id}
              href={media(`${API}/${token}/attachments/${a.id}`)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
              data-testid={`suivi-attachment-${a.id}`}
            >
              <Paperclip className="w-3.5 h-3.5 text-gray-500" />
              <span className="max-w-[160px] truncate">{a.filename}</span>
            </a>
          ))}
        </div>
      )}

      {or_.messages && (
        <PublicMessages
          baseUrl={media(`${API}/${token}/or/${or_.id}/messages`)}
          photoUrl={(mid) => media(`${API}/${token}/messages/${mid}/photo`)}
          accent={accent}
          garageNom={garageNom}
          badge={or_.messages.non_lus_client || 0}
          lang={lang}
          testId={`suivi-messages-${or_.id}`}
        />
      )}

      {/* iter299 — Dossier archivé par le garage : date de clôture, sans bouton d'enlèvement */}
      {or_.archive && !or_.enleve_at && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500" data-testid={`suivi-cloture-${or_.id}`}>
          {t("dossier_cloture_le")} <b className="text-gray-700">{fmtDate(or_.date_fin, lang)}</b>
        </div>
      )}

      {(or_.a_recuperer || or_.enleve_at) && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs">
            {or_.enleve_at ? (
              <span className="text-gray-500">
                {t("vehicule_enleve_le")} <b className="text-gray-700">{fmtDate(or_.enleve_at, lang)}</b>
              </span>
            ) : (
              <span className="font-medium" style={{ color: accent }}>
                {t("vehicule_pret")}
              </span>
            )}
          </div>
          <Button
            onClick={toggleEnleve}
            disabled={enlevant}
            variant={or_.enleve_at ? "outline" : "default"}
            className={`h-9 px-3 text-xs ${or_.enleve_at ? "" : "text-white hover:opacity-90"}`}
            style={or_.enleve_at ? undefined : { backgroundColor: accent }}
            data-testid={`suivi-enleve-${or_.id}`}
          >
            {enlevant
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : or_.enleve_at
                ? t("annuler")
                : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> {t("vehicule_enleve")}</>}
          </Button>
        </div>
      )}

      {/* iter263 — Validation du devis : bouton ou n° d'autorisation selon le réglage client */}
      {or_.validation_mode === "bouton" && or_.devis_a_valider && vis.devis && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-xs text-gray-600">{t("devis_en_attente")}</span>
          <Button
            onClick={validerDevis}
            disabled={validant}
            className="h-9 px-3 text-white hover:opacity-90"
            style={{ backgroundColor: accent }}
            data-testid={`suivi-valider-devis-${or_.id}`}
          >
            {validant ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> {t("valider_devis")}</>}
          </Button>
        </div>
      )}

      {vis.autorisation && or_.validation_mode === "code" && !or_.termine && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="text-[11px] text-gray-500 mb-1">
            {t("votre_autorisation")}
            {or_.devis_a_valider && <span className="text-gray-400"> {t("vaut_accord")}</span>}
          </div>
          <div className="flex gap-2">
            <Input
              value={aut}
              onChange={(e) => setAut(e.target.value)}
              placeholder="Ex. AUT-2026-1234"
              className="h-9 text-sm"
              data-testid={`suivi-autorisation-input-${or_.id}`}
            />
            <Button
              onClick={saveAut}
              disabled={!dirty || saving}
              className="h-9 px-3 text-white hover:opacity-90"
              style={{ backgroundColor: accent }}
              data-testid={`suivi-autorisation-save-${or_.id}`}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("valider")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * iter274 — Bascule FR / DE (mémorisée dans le navigateur du client, donc
 * opérationnelle sur tous les liens déjà partagés).
 */
function LangSwitch({ lang, onLang }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden flex-shrink-0" data-testid="suivi-lang-switch">
      {["fr", "de"].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => onLang(l)}
          className={`px-2 py-1 text-[11px] font-semibold transition-colors ${
            lang === l ? "bg-gray-900 text-white" : "bg-white text-gray-500 hover:bg-gray-50"
          }`}
          data-testid={`suivi-lang-${l}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/**
 * iter272 — Écran d'entrée d'un lien protégé : saisie du mot de passe générique,
 * puis invitation à le remplacer dès la première ouverture.
 */
function SuiviPorte({ token, doitChanger, onConnecte, onChange, lang, onLang }) {
  const t = traducteur(lang);
  const [mdp, setMdp] = useState("");
  const [nouveau, setNouveau] = useState("");
  const [confirme, setConfirme] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const connexion = async (e) => {
    e?.preventDefault();
    setBusy(true); setMsg("");
    try {
      const { data } = await axios.post(`${API}/${token}/acces`, { mot_de_passe: mdp });
      onConnecte(data.acces, data.doit_changer);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setMsg(typeof d === "string" ? d : t("mdp_incorrect"));
    } finally {
      setBusy(false);
    }
  };

  const changer = async (e) => {
    e?.preventDefault();
    if (nouveau.length < 6) return setMsg(t("mdp_court"));
    if (nouveau !== confirme) return setMsg(t("mdp_different"));
    setBusy(true); setMsg("");
    try {
      await http.post(`${API}/${token}/mot-de-passe`, { nouveau });
      toast.success(t("mdp_enregistre"));
      onChange();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setMsg(typeof d === "string" ? d : t("enregistrement_ko"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
      <form
        onSubmit={doitChanger ? changer : connexion}
        className="w-full max-w-sm bg-white rounded-2xl border border-gray-200 p-6 shadow-sm"
        data-testid={doitChanger ? "suivi-mdp-changer" : "suivi-mdp-porte"}
      >
        <div className="flex justify-end">
          <LangSwitch lang={lang} onLang={onLang} />
        </div>
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
          <Lock className="w-5 h-5 text-gray-600" />
        </div>
        <h1 className="text-lg font-semibold text-gray-900 text-center">
          {doitChanger ? t("changer_titre") : t("porte_titre")}
        </h1>
        <p className="text-sm text-gray-500 text-center mt-1">
          {doitChanger ? t("changer_texte") : t("porte_texte")}
        </p>

        {doitChanger ? (
          <div className="mt-4 space-y-2">
            <input
              type="password" autoFocus value={nouveau} onChange={(e) => setNouveau(e.target.value)}
              placeholder={t("changer_nouveau")}
              className="w-full h-11 px-3 rounded-lg border border-gray-300 text-sm focus:border-gray-900 focus:outline-none"
              data-testid="suivi-mdp-nouveau"
            />
            <input
              type="password" value={confirme} onChange={(e) => setConfirme(e.target.value)}
              placeholder={t("changer_confirme")}
              className="w-full h-11 px-3 rounded-lg border border-gray-300 text-sm focus:border-gray-900 focus:outline-none"
              data-testid="suivi-mdp-confirme"
            />
          </div>
        ) : (
          <input
            type="password" autoFocus value={mdp} onChange={(e) => setMdp(e.target.value)}
            placeholder={t("porte_champ")}
            className="w-full h-11 px-3 mt-4 rounded-lg border border-gray-300 text-sm focus:border-gray-900 focus:outline-none"
            data-testid="suivi-mdp-input"
          />
        )}

        {msg && <p className="text-sm text-red-600 mt-2" data-testid="suivi-mdp-erreur">{msg}</p>}

        <Button
          type="submit" disabled={busy}
          className="w-full h-11 mt-4 bg-gray-900 hover:bg-gray-800"
          data-testid="suivi-mdp-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (doitChanger ? t("changer_bouton") : t("porte_bouton"))}
        </Button>
      </form>
    </div>
  );
}

export default function PublicSuivi() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  // iter272 — Porte d'entrée des liens flotte protégés
  const [mdpRequis, setMdpRequis] = useState(false);
  const [doitChanger, setDoitChanger] = useState(false);
  // iter274 — Langue du client (FR / DE), mémorisée dans son navigateur
  const [lang, setLang] = useState(langInitiale);
  const t = traducteur(lang);
  const changeLang = (l) => { memoriseLang(l); setLang(l); };
  const [tab, setTab] = useState("en_cours");
  const [q, setQ] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data: d } = await http.get(`${API}/${token}`);
      setData(d);
      setMdpRequis(false);
      setError("");
    } catch (e) {
      if (e?.response?.status === 401) {
        ACCES = "";
        localStorage.removeItem(accesKey(token));
        setMdpRequis(true);
        setError("");
      } else {
        const d = e?.response?.data?.detail;
        setError(typeof d === "string" ? d : t("lien_invalide"));
      }
    } finally {
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    ACCES = localStorage.getItem(accesKey(token)) || "";
    load();
  }, [load, token]);
  useEffect(() => {
    const id = setInterval(() => load(true), 60000);
    return () => clearInterval(id);
  }, [load]);

  const liste = useMemo(() => {
    const src = tab === "en_cours" ? (data?.en_cours || []) : (data?.termines || []);
    const nq = norm(q);
    if (!nq) return src;
    return src.filter((o) => norm(`${o.plaque}${o.vehicule}${o.numero}${o.client_nom}`).includes(nq));
  }, [data, tab, q]);

  if (mdpRequis || doitChanger) {
    return (
      <SuiviPorte
        token={token}
        lang={lang}
        onLang={changeLang}
        doitChanger={doitChanger}
        onConnecte={(acces, changer) => {
          ACCES = acces || "";
          if (acces) localStorage.setItem(accesKey(token), acces);
          setMdpRequis(false);
          setDoitChanger(!!changer);
          if (!changer) load();
        }}
        onChange={() => { setDoitChanger(false); load(); }}
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
        <div className="text-center max-w-sm" data-testid="suivi-error">
          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-3">
            <Wrench className="w-6 h-6 text-gray-500" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900">{t("lien_indisponible")}</h1>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const accent = data.garage.couleur || "#52a675";
  const vis = data.visibilite || {};
  const nbPrets = data.en_cours.filter((o) => o.a_recuperer).length;
  const nbEnCours = data.en_cours.length - nbPrets;

  return (
    <div className="min-h-screen bg-gray-50 pb-10" data-testid="suivi-page">
      <header className="bg-white border-b border-gray-200">
        <div className="h-1.5" style={{ backgroundColor: accent }} />
        <div className="max-w-3xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {data.garage.logo && (
                <img
                  src={`${API}/${token}/logo`}
                  alt={data.garage.nom}
                  className="h-10 sm:h-11 w-auto max-w-[80px] sm:max-w-[120px] object-contain flex-shrink-0"
                  data-testid="suivi-logo"
                />
              )}
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider font-semibold truncate" style={{ color: accent }}>
                  {data.garage.nom}
                </div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight sm:truncate" data-testid="suivi-title">
                  {t("titre")}
                </h1>
                <p className="text-sm text-gray-500 truncate">{data.label}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <LangSwitch lang={lang} onLang={changeLang} />
              <button
                type="button"
                onClick={() => load()}
                className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
                title={t("actualiser")}
                data-testid="suivi-refresh"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <div className="flex-1 bg-blue-50 rounded-lg px-3 py-2" data-testid="suivi-count-encours">
              <div className="text-lg font-bold text-blue-800 leading-none">{nbEnCours}</div>
              <div className="text-[11px] text-blue-700">{t("en_travaux")}</div>
            </div>
            <div className="flex-1 rounded-lg px-3 py-2" style={{ backgroundColor: `${accent}1a` }} data-testid="suivi-count-prets">
              <div className="text-lg font-bold leading-none" style={{ color: accent }}>{nbPrets}</div>
              <div className="text-[11px]" style={{ color: accent }}>{t("a_recuperer")}</div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 pt-4">
        {vis.termines !== false && (
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => setTab("en_cours")}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                tab === "en_cours" ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200"
              }`}
              data-testid="suivi-tab-encours"
            >
              {t("onglet_en_cours")} ({data.en_cours.length})
            </button>
            <button
              type="button"
              onClick={() => setTab("termines")}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                tab === "termines" ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200"
              }`}
              data-testid="suivi-tab-termines"
            >
              {t("onglet_historique")} ({data.termines.length})
            </button>
          </div>
        )}

        {(data.en_cours.length + data.termines.length) > 6 && (
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("recherche")}
              className="pl-9 bg-white"
              data-testid="suivi-search"
            />
          </div>
        )}

        <div className="space-y-3" data-testid="suivi-list">
          {liste.length === 0 ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center" data-testid="suivi-empty">
              {tab === "en_cours" ? (
                <>
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2" style={{ color: accent }} />
                  <p className="text-sm text-gray-600">{t("vide_en_cours")}</p>
                </>
              ) : (
                <>
                  <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    {t("vide_historique", { mois: data.termines_mois || 12 })}
                  </p>
                </>
              )}
            </div>
          ) : (
            liste.map((o) => (
              <OrCard
                key={o.id}
                or_={o}
                token={token}
                accent={accent}
                vis={vis}
                garageNom={data.garage.nom}
                lang={lang}
                onSaved={() => load(true)}
              />
            ))
          )}
        </div>

        {/* iter293 — chacun règle ses propres notifications (si le garage l'a autorisé) */}
        <div className="mt-6">
          <PublicNotifCard token={token} baseUrl={API} acces={ACCES} />
        </div>

        <p className="text-center text-[11px] text-gray-400 mt-6">
          {data.garage.nom}
          {data.garage.tel ? ` · ${data.garage.tel}` : ""}
          {data.garage.email ? ` · ${data.garage.email}` : ""}
        </p>
      </div>
    </div>
  );
}
